import Flags from "common/constants/flags";
import type { UserNotificationType } from "common/types/userNotification";
import { getToken } from "lib/ctx";
import ReactQueryProvider from "lib/query/providers/ReactQueryProvider";
import UserContextProvider from "lib/state/UserContextProvider";
import type { ReactNode } from "react";
import { useEffect, useRef, useState } from "react";
import AppBanner from "ui/components/AppBanner";
import DragContext from "ui/components/DragContext";
import UserNotification from "ui/components/UserNotification";
import type { AppContextProps } from "ui/hooks/useAppContext";
import { AppContext } from "ui/hooks/useAppContext";
import FullScreenBox from "ui/layouts/FullScreenBox";
import Tooltip from "ui/popovers/Tooltip";
import FetchErrorBoundary from "./FetchErrorBoundary";
import RouterContextSync from "./RouterContextSync";

interface AppContainerProps {
  children: ReactNode;
  isPublicPage: boolean;
}
export default function AppContainer({ children, isPublicPage }: AppContainerProps) {
  const [userNotification, setUserNotification] = useState<UserNotificationType | null>(null);

  const modalContainerRef = useRef(null);

  const [offline, setOffline] = useState<boolean>(false);
  useEffect(() => {
    Flags.print();

    const refreshSession = async () => {
      await getToken();
      setTimeout(
        async () => {
          await refreshSession();
        },
        1000 * 60 * 30,
      );
    };

    if (!isPublicPage) {
      void refreshSession();
    }
  }, []);

  const appContext: AppContextProps = {
    modalContainerRef,
    setUserNotification,
    userNotification,
    offline,
    setOffline,
  };

  const App = (
    <FetchErrorBoundary>
      <Tooltip.Provider>
        <AppBanner>
          <DragContext>
            <FullScreenBox>{children}</FullScreenBox>
          </DragContext>
          <div ref={modalContainerRef} />
          <UserNotification />
        </AppBanner>
      </Tooltip.Provider>
    </FetchErrorBoundary>
  );

  if (isPublicPage) {
    return (
      <ReactQueryProvider>
        <AppContext.Provider value={appContext}>
          <RouterContextSync />
          {App}
        </AppContext.Provider>
      </ReactQueryProvider>
    );
  }

  return (
    <ReactQueryProvider>
      <AppContext.Provider value={appContext}>
        <UserContextProvider>
          <RouterContextSync />
          {App}
        </UserContextProvider>
      </AppContext.Provider>
    </ReactQueryProvider>
  );
}
